import { Api4xxError, Api400Error, Api404Error } from './ApiError';

export type LogLevel = 'info' | 'warn' | 'error';

export interface Logger {
  error: (message: string, meta?: Record<string, unknown>) => void;
  info: (message: string, meta?: Record<string, unknown>) => void;
  warn: (message: string, meta?: Record<string, unknown>) => void;
}

export interface ApiErrorHandlerOptions {
  badRequestLogLevel?: LogLevel;
  logger?: Logger;
  notFoundLogLevel?: LogLevel;
  other4xxLogLevel?: LogLevel;
}

export class ApiErrorHandler {
  private logger?: Logger;
  private notFoundLogLevel: LogLevel;
  private badRequestLogLevel: LogLevel;
  private other4xxLogLevel: LogLevel;

  constructor(options: ApiErrorHandlerOptions = {}) {
    this.logger = options.logger;
    this.notFoundLogLevel = options.notFoundLogLevel || 'info';
    this.badRequestLogLevel = options.badRequestLogLevel || 'warn';
    this.other4xxLogLevel = options.other4xxLogLevel || 'warn';
  }

  private log(level: LogLevel, message: string, meta?: Record<string, unknown>) {
    if (!this.logger) return;
    this.logger[level](message, meta);
  }

  async handleApiError<T>(error: unknown, endpoint: string, args?: Record<string, unknown>): Promise<T | null> {
    if (!(error instanceof Response)) {
      throw error;
    }

    const { status } = error;

    if (status === 404) {
      const apiError = new Api404Error(endpoint, args, error);
      this.log(this.notFoundLogLevel, apiError.message, {
        args,
        endpoint,
        statusCode: status,
      });
      return null;
    }

    if (status === 400) {
      const apiError = new Api400Error(endpoint, args, error);
      this.log(this.badRequestLogLevel, apiError.message, {
        args,
        endpoint,
        statusCode: status,
      });
      return null;
    }

    if (status >= 400 && status < 500) {
      const apiError = new Api4xxError(status, endpoint, args, error);
      this.log(this.other4xxLogLevel, apiError.message, {
        args,
        endpoint,
        statusCode: status,
      });
      return null;
    }

    throw error;
  }
}
